import SEO from "../components/SEO";
import { COMPANY_INFO } from "../data/companyData";
import { getOptimizedImageUrl } from "../lib/cms";
import ContactForm from "../components/ContactForm";

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Kontakt - Tømrermester Jacob"
        description="Kontakt Tømrermester Søren Emil v/Jacob Heiselberg i Ribe for et uforpligtende tilbud på tømrerarbejde, tag, vinduer eller renovering."
        ogTitle="Kontakt - Tømrermester Jacob"
        ogDescription="Tømrermester Søren Emil v/Jacob Heiselberg Jacob Rokkjær Heiselberg"
      />

      {/* Hero */}
      <section className="bg-stone-100/60 border-b border-stone-200 py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-100 text-[#ce0611] text-xs font-semibold uppercase tracking-wider mb-4">
            <i className="fa-solid fa-envelope text-[#ce0611]" aria-hidden="true"></i>
            <span>Kontakt & Dialog</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 mb-4">
            Kontakt os
          </h1>
          <p className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Vi står altid klar med faglig rådgivning og udarbejder gerne et uforpligtende tilbud på din bygge- eller tømreropgave.
          </p>
        </div>
      </section>

      {/* Main contact section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            {/* Left Col: Company info & firm vehicle */}
            <div className="lg:col-span-5 space-y-8">
              <div className="p-6 sm:p-8 rounded-2xl bg-stone-50 border border-stone-200/90 space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-stone-900">
                    Tømrermester Søren Emil v/Jacob Heiselberg
                  </h2>
                  <p className="text-sm font-medium text-[#ce0611] mt-0.5">
                    {COMPANY_INFO.owner}
                  </p>
                </div>

                <div className="space-y-4 text-sm text-stone-700">
                  <div className="flex items-start space-x-3.5">
                    <div className="w-9 h-9 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone" aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="text-xs text-stone-500 font-semibold uppercase tracking-wider">Telefon</div>
                      <a
                        href={`tel:${COMPANY_INFO.phoneRaw}`}
                        className="text-stone-900 font-semibold hover:text-[#ce0611] transition-colors whitespace-nowrap text-base"
                      >
                        <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3.5">
                    <div className="w-9 h-9 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="text-xs text-stone-500 font-semibold uppercase tracking-wider">E-mail</div>
                      <a
                        href={`mailto:${COMPANY_INFO.email}`}
                        className="text-stone-900 font-semibold hover:text-[#ce0611] transition-colors whitespace-nowrap"
                      >
                        <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3.5">
                    <div className="w-9 h-9 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="text-xs text-stone-500 font-semibold uppercase tracking-wider">Adresse</div>
                      <a
                        href={COMPANY_INFO.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-stone-900 hover:text-[#ce0611] transition-colors leading-relaxed block"
                      >
                        {COMPANY_INFO.address}
                        <br />
                        {COMPANY_INFO.city}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3.5 pt-2 border-t border-stone-200">
                    <div className="w-9 h-9 rounded-full bg-stone-200 text-stone-700 flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-id-card" aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="text-xs text-stone-500 font-semibold uppercase tracking-wider">CVR-nummer</div>
                      <div className="text-stone-800 font-medium">{COMPANY_INFO.cvr}</div>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <a
                    href={COMPANY_INFO.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#ce0611] hover:text-[#b0040e] transition-colors"
                  >
                    <i className="fa-solid fa-map-location-dot mr-1.5" aria-hidden="true"></i>
                    <span>Find vej på Google Maps</span>
                  </a>
                </div>
              </div>

              {/* Firm vehicle image from scrape */}
              <div className="rounded-2xl overflow-hidden shadow-md border border-stone-200 bg-stone-100 aspect-[16/10]">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/snedkerfirma-bil-toemrermester-jacob.jpg",
                    { width: 700 }
                  )}
                  alt="Tømrermester Jacob firmabil"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Col: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm
                title="Skriv til os"
                subtitle="Udfyld nedenstående formular og så vender vi tilbage hurtigst muligt."
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
